import { SERVER, AUTH_PATH } from "appConfig";

import { i18n } from "services/i18nService";
import { retrieveSettings } from "services/sosInventoryService/settings";
import { getSupportPrivileges } from "services/sosInventoryService/sosApi";
import {
  setLastLoginCookie,
  getTokenParams,
  setBearerToken,
} from "services/utility/authentication";

import {
  setNeedsCompanyChooser,
  setIsLoggedIn,
} from "globalState/authenticationSlice";
import globalState from "globalState/globalState";
import {
  setUser,
  setCompany,
  setCompanyList,
  setSupportPrivileges,
} from "globalState/userCompanySettingsSlice";

import {
  SOS_EMAIL_DOMAIN,
  EMAIL_NOT_VERIFIED_INTUIT_STRING,
} from "appConstants";

// move to src/services/utility/authentication.js once
// https://app.clickup.com/t/868eq8jx5 complete
export async function thirdPartyLogin(
  authCode,
  realmId,
  error,
  errorHandler,
  loadingHandler
) {
  loadingHandler(true);
  // env is used by BE/QBO to validate the redirect URI
  // possible values - "prod", "beta", "test", and "sandbox"
  // each of these values correspond to a white listed redirect URI for QBO
  // "sandbox" not working until https://app.clickup.com/t/2p83rup complete
  const env = process.env.REACT_APP_ENVIRONMENT;
  const body = JSON.stringify({ authCode, realmId, error, env });
  const response = await fetch(SERVER + AUTH_PATH, { method: "POST", body });

  if (response.status === 500) {
    errorHandler(i18n("auth.IntuitError"));
    return;
  }

  const loginResponse = await response.json();
  if (response.status === 401) {
    const unauthorizedMessage =
      loginResponse?.status === EMAIL_NOT_VERIFIED_INTUIT_STRING
        ? EMAIL_NOT_VERIFIED_INTUIT_STRING
        : i18n("auth.IntuitError");
    errorHandler(unauthorizedMessage);
    return;
  }

  const loginError = validateLoginResponse(loginResponse);
  if (loginError) {
    errorHandler(loginError);
    loadingHandler(false);
    return;
  }

  const { name, userEmail } = loginResponse;
  globalState.dispatch(setUser({ name, email: userEmail }));

  const { selectedCompanyId, company, companies, needsCompanyChooser } =
    getCompanyData(loginResponse);
  globalState.dispatch(setNeedsCompanyChooser(needsCompanyChooser));
  globalState.dispatch(setCompany(company));
  globalState.dispatch(setCompanyList(companies));

  await updateTokens({
    email: loginResponse.userEmail,
    loginResponse,
    selectedCompanyId,
    errorHandler,
    grantType: "client_credentials",
  });

  if (userEmail.endsWith(SOS_EMAIL_DOMAIN)) {
    const { privileges } = await getSupportPrivileges();
    globalState.dispatch(setSupportPrivileges(privileges));
  }

  await retrieveSettings();

  setLastLoginCookie();
  loadingHandler(false);
  globalState.dispatch(setIsLoggedIn("yes"));

  return;
}

// Remove in favor of the updateToken in
// src/services/utility/authentication.js once
// https://app.clickup.com/t/868eq8jx5 complete
async function updateTokens({
  email,
  password,
  loginResponse,
  selectedCompanyId,
  errorHandler,
  grantType,
}) {
  let tokens = {};

  const { companyIds, accessTokenSecrets, accessTokens } = loginResponse;
  const promises = companyIds.map(async (companyId, index) => {
    const params = getTokenParams(grantType, {
      email,
      password,
      accessToken: accessTokens[index],
      accessTokenSecret: accessTokenSecrets[index],
      scope: companyId,
    });
    const body = Object.keys(params)
      .map((key) => key + "=" + encodeURIComponent(params[key]))
      .join("&");

    const response = await fetch(SERVER + "/oauth2/token", {
      method: "POST",
      body,
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Host: SERVER,
      },
      credentials: "include",
    });
    const json = await response.json();
    if (response.ok) {
      const token = {
        email,
        token: json.access_token,
        refresh: json.refresh_token,
      };
      tokens[companyId] = token;
    } else {
      errorHandler(json.error_description);
    }
  });

  await Promise.all(promises);
  setBearerToken(tokens[selectedCompanyId]?.token);
}

// Remove in favor of the getCompanyData in
// src/services/utility/authentication.js once
// https://app.clickup.com/t/868eq8jx5 complete
function getCompanyData(loginResponse) {
  let selectedCompanyIdx = 0;
  let needsCompanyChooser;
  if (loginResponse.defaultCompanyIndex > -1) {
    selectedCompanyIdx = loginResponse.defaultCompanyIndex;
    needsCompanyChooser = false;
  } else if (loginResponse.companyIds.length === 1) {
    needsCompanyChooser = false;
  } else {
    needsCompanyChooser = true;
  }
  const selectedCompanyId = loginResponse.companyIds[selectedCompanyIdx];
  let companies = [];
  for (let idx = 0; idx < loginResponse.companyIds.length; idx++) {
    const company = {
      id: loginResponse.companyIds[idx],
      userId: loginResponse.userIds[idx],
      name: loginResponse.companyNames[idx],
      accessToken: loginResponse.accessTokens[idx],
      accessTokenSecret: loginResponse.accessTokenSecrets[idx],
      expirationDate: loginResponse.expirationDates[idx],
      userPrivileges: loginResponse.permissionsList[idx],
      planLevel: loginResponse.planLevels[idx],
      status: loginResponse.statusList[idx],
      accountNumber: loginResponse.acctNumbers[idx],
      default: idx === loginResponse.defaultCompanyIndex,
    };
    companies.push(company);
  }
  const company = companies[selectedCompanyIdx];
  companies = companies.sort((a, b) => (a.name < b.name ? -1 : 1));

  return { selectedCompanyId, company, companies, needsCompanyChooser };
}

// Remove in favor of the validateLoginResponse in
// src/services/utility/authentication.js once
// https://app.clickup.com/t/868eq8jx5 complete
function validateLoginResponse(loginResponse) {
  if (loginResponse.status === "Error: Unknown error.") {
    return i18n("global.SomethingWentWrong");
  } else if (loginResponse.status === "invalid") {
    return i18n("auth.Invalid");
  } else if (loginResponse.status === "locked") {
    return i18n("auth.Locked");
  } else if (!loginResponse.companyIds.length) {
    return i18n("auth.NonUser");
  }
  return null;
}
